// Settings › Dispatch › Channel cut-offs & dashboard metrics (Alex,
// 2026-10-13). Per warehouse: the default weekday grid + ship cut-off,
// and one row per sales channel (every channel seen on orders in the
// last 90 days) with its "On dashboard metrics" tick and either the
// warehouse default grid or its own. Every save writes TODAY's version
// on the server; earlier days keep the rule that was in force, so the
// page says so next to each save.
//
// Data: GET/PUT /dispatch-admin/channel-rules (api/dispatchChannelRules.ts).
import { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, Pencil, RotateCcw, X } from "lucide-react";
import { toast } from "react-toastify";

import { useTranslation } from "../../../i18n";
import { useReadOnly } from "../../../access/ReadOnly";
import { useWarehouseScope } from "../../../access/useWarehouseScope";
import { apiFetch } from "../../../api/apiFetch";
import {
    ChannelRule, ChannelRulesPayload, RuleGrid, RuleVersion, Weekday,
    DEFAULT_GRID, DEFAULT_SHIP_CUTOFF, RULE_SERVICES, WEEKDAYS,
    cloneGrid, fetchChannelRules, gridProblem, saveChannelRule, shipDayLabel, weekdayLabel,
} from "../../../api/dispatchChannelRules";

interface AllSettingsRow { warehouse: string; }

const SHIP_DAY_OPTIONS = [0, 1, 2, 3, 4, 5, 6];

// ── Grid editor ──────────────────────────────────────────────────────
interface GridEditorProps {
    grid: RuleGrid;
    onChange: (next: RuleGrid) => void;
    disabled: boolean;
}

const GridEditor = ({ grid, onChange, disabled }: GridEditorProps) => {
    const { t } = useTranslation();
    const setCell = (svc: "standard" | "express", d: Weekday, patch: Partial<{ cutoff: string | null; ship_days: number }>) => {
        const next = cloneGrid(grid);
        next[svc][d] = { ...next[svc][d], ...patch };
        onChange(next);
    };
    return (
        <div className="admin-settings-table admin-settings-table--5col channel-rules-grid">
            <div className="admin-settings-row admin-settings-row--head">
                <div>{t("channel_rules_day_col")}</div>
                <div>{t("channel_rules_standard")} · {t("channel_rules_cutoff_col")}</div>
                <div>{t("channel_rules_standard")} · {t("channel_rules_ships_col")}</div>
                <div>{t("channel_rules_express")} · {t("channel_rules_cutoff_col")}</div>
                <div>{t("channel_rules_express")} · {t("channel_rules_ships_col")}</div>
            </div>
            {WEEKDAYS.map((d) => (
                <div className="admin-settings-row" key={d}>
                    <div className="admin-settings-warehouse">{weekdayLabel(d)}</div>
                    {RULE_SERVICES.map((svc) => {
                        const cell = grid[svc][d];
                        return [
                            <div key={`${svc}-cutoff`}>
                                <input
                                    type="time"
                                    className="admin-settings-input"
                                    value={cell.cutoff ?? ""}
                                    placeholder={t("channel_rules_no_cutoff")}
                                    disabled={disabled}
                                    aria-label={`${weekdayLabel(d)} ${t(`channel_rules_${svc}`)} ${t("channel_rules_cutoff_col")}`}
                                    onChange={(e) => setCell(svc, d, { cutoff: e.target.value ? e.target.value : null })}
                                />
                            </div>,
                            <div key={`${svc}-ships`}>
                                <select
                                    className="admin-settings-select"
                                    value={cell.ship_days}
                                    disabled={disabled}
                                    aria-label={`${weekdayLabel(d)} ${t(`channel_rules_${svc}`)} ${t("channel_rules_ships_col")}`}
                                    onChange={(e) => setCell(svc, d, { ship_days: Number(e.target.value) })}
                                >
                                    {SHIP_DAY_OPTIONS.map((n) => (
                                        <option key={n} value={n}>
                                            {shipDayLabel(d, n)}{n === 0 ? ` (${t("channel_rules_same_day")})` : ` (+${n})`}
                                        </option>
                                    ))}
                                </select>
                            </div>,
                        ];
                    })}
                </div>
            ))}
        </div>
    );
};

// A compact read-only rendering of a grid ("Mon 13:00 → Tue · Tue 13:00 → Tue …").
const GridSummary = ({ grid }: { grid: RuleGrid }) => {
    const { t } = useTranslation();
    return (
        <div className="channel-rules-summary">
            {RULE_SERVICES.map((svc) => (
                <div key={svc} className="channel-rules-summary-line">
                    <strong>{t(`channel_rules_${svc}`)}:</strong>{" "}
                    {WEEKDAYS.map((d) => {
                        const c = grid[svc][d];
                        return `${weekdayLabel(d)} ${c.cutoff ?? "—"} → ${shipDayLabel(d, c.ship_days)}`;
                    }).join(" · ")}
                </div>
            ))}
        </div>
    );
};

const versionNote = (t: (k: string, p?: Record<string, string>) => string, v: RuleVersion | null): string => {
    if (!v || v.builtin || !v.effective_from) return t("channel_rules_builtin");
    const since = t("channel_rules_since", { date: v.effective_from });
    return v.created_by_name ? `${since} ${t("channel_rules_by", { name: v.created_by_name })}` : since;
};

// ── Page ─────────────────────────────────────────────────────────────
const ChannelRulesAdmin = () => {
    const { t } = useTranslation();
    const readOnly = useReadOnly();
    const { own, limit } = useWarehouseScope();
    const [warehouses, setWarehouses] = useState<string[]>([]);
    const [warehouse, setWarehouse] = useState<string>("");
    const [data, setData] = useState<ChannelRulesPayload | null>(null);
    const [loading, setLoading] = useState(true);
    const [savingKey, setSavingKey] = useState<string | null>(null);
    const [defaultDraft, setDefaultDraft] = useState<{ grid: RuleGrid; ship: string } | null>(null);
    const [editingChannel, setEditingChannel] = useState<string | null>(null);
    const [channelDraft, setChannelDraft] = useState<RuleGrid | null>(null);

    // Warehouse list (the caller's own only when they may not cross).
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await apiFetch(`/settings`, {});
                if (!res.ok) throw new Error("load");
                const rows: AllSettingsRow[] = await res.json();
                const names = limit(rows.map((r) => r.warehouse)).sort();
                if (cancelled) return;
                setWarehouses(names);
                setWarehouse((cur) => cur || (names.includes(own) ? own : names[0] ?? ""));
            } catch {
                if (!cancelled) toast.error(t("server_error"), { toastId: "channel-rules-wh-err" });
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const load = useCallback(async (wh: string) => {
        if (!wh) return;
        setLoading(true);
        try {
            const payload = await fetchChannelRules(wh);
            setData(payload);
            setDefaultDraft(null);
            setEditingChannel(null);
            setChannelDraft(null);
        } catch {
            toast.error(t("server_error"), { toastId: "channel-rules-load-err" });
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => { load(warehouse); }, [warehouse, load]);

    const defaultGrid: RuleGrid = useMemo(() => data?.default.grid ?? DEFAULT_GRID, [data]);
    const defaultShip = data?.default.ship_cutoff ?? DEFAULT_SHIP_CUTOFF;

    const save = async (key: string, input: Parameters<typeof saveChannelRule>[0]) => {
        setSavingKey(key);
        try {
            const out = await saveChannelRule(input);
            setData(out.rules);
            toast.success(t("channel_rules_saved"), { toastId: "channel-rules-saved" });
            return true;
        } catch (err) {
            toast.error(err instanceof Error && err.message ? err.message : t("server_error"), { toastId: "channel-rules-save-err" });
            return false;
        } finally {
            setSavingKey(null);
        }
    };

    // ── Default card actions ──
    const startDefaultEdit = () => setDefaultDraft({ grid: cloneGrid(defaultGrid), ship: defaultShip });
    const saveDefault = async () => {
        if (!defaultDraft || !data) return;
        const problem = gridProblem(defaultDraft.grid);
        if (problem) { toast.error(t(problem.key, problem.params), { toastId: "channel-rules-grid-err" }); return; }
        if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(defaultDraft.ship)) { toast.error(t("channel_rules_ship_cutoff"), { toastId: "channel-rules-grid-err" }); return; }
        const ok = await save("*", {
            warehouse: data.warehouse, channel_key: "*", include_in_metrics: true,
            grid: defaultDraft.grid, ship_cutoff: defaultDraft.ship,
        });
        if (ok) setDefaultDraft(null);
    };

    // ── Channel row actions ──
    const toggleMetrics = (c: ChannelRule) => {
        if (!data) return;
        const current = c.current?.include_in_metrics ?? true;
        save(c.channel_key, {
            warehouse: data.warehouse, channel_key: c.channel_key,
            include_in_metrics: !current, grid: c.current?.grid ?? null,
        });
    };
    const startChannelEdit = (c: ChannelRule) => {
        setEditingChannel(c.channel_key);
        setChannelDraft(cloneGrid(c.current?.grid ?? defaultGrid));
    };
    const saveChannelGrid = async (c: ChannelRule) => {
        if (!data || !channelDraft) return;
        const problem = gridProblem(channelDraft);
        if (problem) { toast.error(t(problem.key, problem.params), { toastId: "channel-rules-grid-err" }); return; }
        const ok = await save(c.channel_key, {
            warehouse: data.warehouse, channel_key: c.channel_key,
            include_in_metrics: c.current?.include_in_metrics ?? true, grid: channelDraft,
        });
        if (ok) { setEditingChannel(null); setChannelDraft(null); }
    };
    const revertToDefaultGrid = async (c: ChannelRule) => {
        if (!data) return;
        const ok = await save(c.channel_key, {
            warehouse: data.warehouse, channel_key: c.channel_key,
            include_in_metrics: c.current?.include_in_metrics ?? true, grid: null,
        });
        if (ok) { setEditingChannel(null); setChannelDraft(null); }
    };

    const busy = savingKey !== null;

    return (
        <div className="channel-rules">
            {warehouses.length > 1 && (
                <label className="admin-settings-field">
                    <span>{t("warehouse_col")}</span>
                    <select
                        className="admin-settings-select"
                        value={warehouse}
                        onChange={(e) => setWarehouse(e.target.value)}
                        disabled={loading || busy}
                    >
                        {warehouses.map((w) => <option key={w} value={w}>{w}</option>)}
                    </select>
                </label>
            )}
            {warehouses.length === 0 && !loading && (
                <div className="admin-settings-empty">{t("no_warehouses")}</div>
            )}

            {loading && <div className="admin-settings-section-hint"><Loader2 size={14} className="spin" /> {t("loading")}</div>}

            {data && !loading && (
                <>
                    <p className="admin-settings-section-hint">{t("channel_rules_applies_from", { date: data.today })}</p>

                    {/* ── Warehouse default ── */}
                    <section className="channel-rules-card">
                        <div className="channel-rules-card-head">
                            <h4 className="admin-settings-section-title">{t("channel_rules_default_title")}</h4>
                            <span className="channel-rules-meta">{versionNote(t, data.default)}</span>
                            {!readOnly && defaultDraft === null && (
                                <button type="button" className="btn btn-sm btn-secondary" onClick={startDefaultEdit} disabled={busy}>
                                    <Pencil size={14} /> {t("edit")}
                                </button>
                            )}
                        </div>
                        <p className="admin-settings-section-hint">{t("channel_rules_default_hint")}</p>
                        {defaultDraft === null ? (
                            <>
                                <div className="channel-rules-ship">
                                    <strong>{t("channel_rules_ship_cutoff")}:</strong> {defaultShip}
                                </div>
                                <GridSummary grid={defaultGrid} />
                            </>
                        ) : (
                            <>
                                <label className="admin-settings-field channel-rules-ship">
                                    <span>{t("channel_rules_ship_cutoff")}</span>
                                    <input
                                        type="time"
                                        className="admin-settings-input"
                                        value={defaultDraft.ship}
                                        disabled={busy}
                                        onChange={(e) => setDefaultDraft({ ...defaultDraft, ship: e.target.value })}
                                    />
                                </label>
                                <GridEditor grid={defaultDraft.grid} onChange={(g) => setDefaultDraft({ ...defaultDraft, grid: g })} disabled={busy} />
                                <div className="channel-rules-actions">
                                    <button type="button" className="btn btn-sm btn-primary" onClick={saveDefault} disabled={busy}>
                                        {savingKey === "*" ? <Loader2 size={14} className="spin" /> : null} {t("save")}
                                    </button>
                                    <button type="button" className="btn btn-sm btn-ghost" onClick={() => setDefaultDraft({ grid: cloneGrid(DEFAULT_GRID), ship: DEFAULT_SHIP_CUTOFF })} disabled={busy}>
                                        <RotateCcw size={14} /> {t("reset")}
                                    </button>
                                    <button type="button" className="btn btn-sm btn-ghost" onClick={() => setDefaultDraft(null)} disabled={busy}>
                                        <X size={14} /> {t("cancel")}
                                    </button>
                                </div>
                            </>
                        )}
                        {data.default.versions.length > 1 && (
                            <details className="channel-rules-history">
                                <summary>{t("channel_rules_history")}</summary>
                                <ul>
                                    {data.default.versions.map((v) => (
                                        <li key={v.id ?? v.effective_from ?? "builtin"}>
                                            {versionNote(t, v)} · {t("channel_rules_ship_cutoff")} {v.ship_cutoff ?? DEFAULT_SHIP_CUTOFF}
                                        </li>
                                    ))}
                                </ul>
                            </details>
                        )}
                    </section>

                    {/* ── Channels ── */}
                    <section className="channel-rules-card">
                        <h4 className="admin-settings-section-title">{t("channel_rules_channels_title")}</h4>
                        {data.channels.length === 0 ? (
                            <div className="admin-settings-empty">{t("channel_rules_no_channels")}</div>
                        ) : (
                            <div className="admin-settings-table admin-settings-table--5col channel-rules-channels">
                                <div className="admin-settings-row admin-settings-row--head">
                                    <div>{t("channel_rules_channel_col")}</div>
                                    <div>{t("channel_rules_orders_col")}</div>
                                    <div>{t("channel_rules_metrics_col")}</div>
                                    <div>{t("channel_rules_grid_col")}</div>
                                    <div />
                                </div>
                                {data.channels.map((c) => {
                                    const included = c.current?.include_in_metrics ?? true;
                                    const custom = c.current?.grid !== null && c.current?.grid !== undefined;
                                    const editing = editingChannel === c.channel_key && channelDraft !== null;
                                    const saving = savingKey === c.channel_key;
                                    return (
                                        <div key={c.channel_key} className="channel-rules-channel">
                                            <div className="admin-settings-row">
                                                <div className="admin-settings-warehouse">
                                                    <div>{c.display_name}</div>
                                                    <div className="channel-rules-meta">{c.current ? versionNote(t, c.current) : t("channel_rules_not_saved")}</div>
                                                </div>
                                                <div>{c.orders.toLocaleString()}</div>
                                                <div>
                                                    <label className="admin-settings-toggle">
                                                        <input
                                                            type="checkbox"
                                                            checked={included}
                                                            disabled={readOnly || busy}
                                                            aria-label={`${c.display_name} ${t("channel_rules_metrics_col")}`}
                                                            onChange={() => toggleMetrics(c)}
                                                        />
                                                        <span className="admin-settings-slider" />
                                                    </label>
                                                </div>
                                                <div>{custom ? t("channel_rules_custom") : t("channel_rules_uses_default")}</div>
                                                <div className="channel-rules-actions">
                                                    {saving && <Loader2 size={14} className="spin" />}
                                                    {!readOnly && !editing && (
                                                        <button type="button" className="btn btn-sm btn-secondary" onClick={() => startChannelEdit(c)} disabled={busy}>
                                                            <Pencil size={14} /> {t("channel_rules_edit_grid")}
                                                        </button>
                                                    )}
                                                    {!readOnly && custom && !editing && (
                                                        <button type="button" className="btn btn-sm btn-ghost" onClick={() => revertToDefaultGrid(c)} disabled={busy}>
                                                            <RotateCcw size={14} /> {t("channel_rules_use_default")}
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                            {custom && !editing && c.current?.grid && <GridSummary grid={c.current.grid} />}
                                            {editing && channelDraft && (
                                                <div className="channel-rules-editor">
                                                    <GridEditor grid={channelDraft} onChange={setChannelDraft} disabled={busy} />
                                                    <div className="channel-rules-actions">
                                                        <button type="button" className="btn btn-sm btn-primary" onClick={() => saveChannelGrid(c)} disabled={busy}>
                                                            {saving ? <Loader2 size={14} className="spin" /> : null} {t("save")}
                                                        </button>
                                                        <button type="button" className="btn btn-sm btn-ghost" onClick={() => setChannelDraft(cloneGrid(defaultGrid))} disabled={busy}>
                                                            <RotateCcw size={14} /> {t("channel_rules_uses_default")}
                                                        </button>
                                                        <button type="button" className="btn btn-sm btn-ghost" onClick={() => { setEditingChannel(null); setChannelDraft(null); }} disabled={busy}>
                                                            <X size={14} /> {t("cancel")}
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                            {c.versions.length > 1 && (
                                                <details className="channel-rules-history">
                                                    <summary>{t("channel_rules_history")}</summary>
                                                    <ul>
                                                        {c.versions.map((v) => (
                                                            <li key={v.id ?? v.effective_from ?? "x"}>
                                                                {versionNote(t, v)} · {v.include_in_metrics ? t("channel_rules_metrics_col") : `✕ ${t("channel_rules_metrics_col")}`}
                                                                {" · "}{v.grid ? t("channel_rules_custom") : t("channel_rules_uses_default")}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </details>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </section>
                </>
            )}
        </div>
    );
};

export default ChannelRulesAdmin;
